import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { EmailTemplatesContext } from '../../../contexts/email-templates.context';
import { EmailTemplate } from '../../../../shared/models/generated/ziroModels';
import { TranslateService } from '@ngx-translate/core';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../../shared/bottom-nav/bottom-nav.service';
import { Subscription } from 'rxjs';
import { DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';

interface EmailTemplateRow extends DatatableRow {
  name: string;
  recipients: string[];
}

@Component({
  selector: 'ziro-email-templates-table',
  templateUrl: 'email-templates-table.component.html',
  styleUrls: ['../../../admin-page.scss'],
  standalone: false,
})
export class EmailTemplatesTableComponent implements OnInit, OnDestroy {
  private emailTemplatesContext = inject(EmailTemplatesContext);
  private translateService = inject(TranslateService);
  private breadcrumbsService = inject(BreadcrumbsService);
  private bottomNavService = inject(BottomNavService);

  isLoading = true;
  tableDataAutomation = 'email-template-table';
  columns: DatatableColumn<EmailTemplateRow>[] = [
    {
      name: 'name',
      label: 'tkey;admin.email_templates.table_view.table.headers.name',
      columnSize: 'col-sm-5',
    },
    {
      name: 'recipients',
      label: 'tkey;admin.email_templates.table_view.table.headers.recipients',
      columnSize: 'col-sm-5',
      sortFn: (a: string[], b: string[]) => a[0]?.localeCompare(b[0]),
    },
  ];
  data: EmailTemplateRow[] = [];

  private _subscription = new Subscription();

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.email_templates.title' }]);
    this._initBottomNav();

    const templateStateSub = this.emailTemplatesContext.state$.subscribe((templates) => {
      this._buildTableRows(templates);
      this.isLoading = false;
    });
    this._subscription.add(templateStateSub);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private _buildTableRows(emailTemplates: EmailTemplate[]): void {
    const endUserRecipientLabel = this.translateService.instant(
      'tkey;admin.email_templates.table_view.table.360_view_recipient_badge',
    );

    this.data = emailTemplates.map((emailTemplate): EmailTemplateRow => {
      const recipients = emailTemplate.is360ViewEndUserRecipient
        ? emailTemplate.otherRecipients.concat([endUserRecipientLabel])
        : emailTemplate.otherRecipients;

      return {
        name: emailTemplate.emailTemplateName,
        recipients,
        badges: {
          recipients: {
            maxLength: 30,
            values: recipients.map((recipient) => ({
              label: recipient,
              cssClass: 'bg-secondary',
            })),
          },
        },
        actions: [
          {
            dataAutomation: 'email-templates-copy-button',
            style: ButtonStyles.OUTLINE_DEFAULT,
            type: ButtonTypes.LINK,
            icon: ZiroIcons.COPY,
            href: window.location.href + `/template/${emailTemplate.id}/copy`,
          },
          {
            dataAutomation: 'email-templates-edit-button',
            style: ButtonStyles.OUTLINE_PRIMARY,
            type: ButtonTypes.LINK,
            icon: ZiroIcons.EDIT,
            href: window.location.href + `/template/${emailTemplate.id}`,
          },
          {
            dataAutomation: 'email-templates-delete-button',
            style: ButtonStyles.OUTLINE_DANGER,
            type: ButtonTypes.BUTTON,
            icon: ZiroIcons.DELETE_SMALL,
            onClick: () => this.emailTemplatesContext.deleteTemplate(emailTemplate),
          },
        ],
      };
    });
  }

  private _initBottomNav(): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'email-templates-add-button',
          label: 'tkey;admin.email_templates.table_view.add_button.label',
          icon: ZiroIcons.ADD,
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'email-templates-add-button',
          type: ButtonTypes.LINK,
          href: window.location.href + '/template',
        },
      ]),
    );
  }
}
